import React, { useState, useEffect, useContext } from 'react';
import liff from '@line/liff';
import styles from './PaymentForm.module.scss';
import { split_into } from '../../../utilities/Calculator';
import {themeColors, radius} from '../../../constants';
import { useParams, useHistory as useRouterHistory } from 'react-router-dom';
import { Context as PaymentContext } from '../../../contexts/PaymentContext';
import { sumOwers, sumPaymentObjects } from '../../../utilities/PaymentFormHelper';
import {
  FullPageLoader,
  Section,
  DatePickerInput,
  Svg,
  Button,
  TextInput,
  CurrencyDropdown,
  InvoiceScanner,
  OwerEditableCheckboxSelectLabel,
  PayerEditableCheckboxSelectLabel,
} from '../../../components';
import {
  useHistory,
  useUserRadioSelectLabel,
  useExchangeRates,
  useCurrencies,
} from '../../../hooks';
import { unitRate } from '../../../utilities/convertCurrency';

const PaymentForm = ({ users, manualOwers, owers, payment, authState, customPayers, onComplete }) => {
  const debug = false;
  const [disableForm, setDisableForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fixedAmount, setFixedAmount] = useState(null);
  // Foreign-currency origin metadata. Set when a non-settlement currency is
  // picked; null otherwise (the typed number is then the settlement amount).
  const [originMeta, setOriginMeta] = useState(null);
  // The currency selected in the inline dropdown (defaults to the book's
  // settlement currency) and the foreign amount typed for it.
  const [payCurrency, setPayCurrency] = useState(null);
  const [foreignAmount, setForeignAmount] = useState('');
  const [favorites, setFavorites] = useState([]);
  const { rates: fxRates } = useExchangeRates(authState);
  const { byCode: currencyMeta } = useCurrencies(authState);
  const { group_id, accounting_book_id } = useParams();
  const history = useHistory();
  const routerHistory = useRouterHistory();
  const {
    state,
    validateForm,
    createPayment,
    resetForm
  } = useContext(PaymentContext);

  /* eslint-disable no-unused-vars */
  const [_name, _setName] = useState({ value: '', valid: true });
  const [_allocationType, _setAllocationType] = useState(0); // 0 - by acount, 1 - by piece
  const [_payerAllocationType, _setPayerAllocationType] = useState(0); // 0 - by acount, 1 - by piece
  const [formTouched, setFormTouched] = useState(false);
  const [_amount, _setAmount] = useState({ value: '', valid: true });
  const [_creation_date, _setCreationDate] = useState({ value: null, valid: true });
  const [_owers, _setOwers] = useState({ value: [], valid: true });
  const [_manualOwers, _setManualOwers] = useState({ value: [], valid: true });
  const [_payers, _setPayers] = useState({ value: [], valid: true });
  const [_customPayers, _setCustomPayers] = useState({ value: [], valid: true });
  const [summaryAmount, setSummaryAmount] = useState(null);
  const [payerSummaryAmount, setPayerSummaryAmount] = useState(null);
  /* eslint-disable no-unused-vars */
  const [_payer, _setPayer] = useState({ value: null, valid: true });
  const [alertMessage, setAlertMessage] = useState(null);
  const [_users, setUsers] = useState([]);
  const [summary, setSummary] = useState({
    valid: true,
    message: '總額與分帳小計不符，請調整'
  });

  let use_multi_payer = false;
  let locked = false;
  if (state.accounting_book_details) {
    use_multi_payer = state.accounting_book_details.use_multi_payer;
    locked = state.accounting_book_details.lock_status === 'locked';
  }

  useEffect(() => {
    if (users.length !== _users.length) {
      setUsers(users);
      setDisableForm(false);
    }
  }, [_users.length, users, users.length]);

  useEffect(() => {
    // edit existing payment prefill
    if (payment) {
      setDisableForm(true);
      _setName({ value: payment.description, valid: true });
      // For a foreign payment the editor is canonical in the selected currency,
      // so seed the total from origin_amount (not the settlement payment.amount).
      const workingTotal = payment.origin_currency ? payment.origin_amount : payment.amount;
      _setAmount({ value: workingTotal, valid: true });
      setFixedAmount(workingTotal);
      setSummaryAmount(workingTotal);
      setPayerSummaryAmount(workingTotal);
      _setCreationDate({ value: payment.paid_at, valid: true });
      if (payment.origin_currency) {
        setOriginMeta({
          origin_currency: payment.origin_currency,
          origin_amount: payment.origin_amount,
          exchange_rate: payment.exchange_rate,
        });
        setPayCurrency(payment.origin_currency);
        setForeignAmount(String(payment.origin_amount));
      }
      setDisableForm(false);
    }
  }, [payment, _setName, _setAmount, _setCreationDate, customPayers]);

  // Default the inline currency dropdown to the book's settlement currency once
  // the book loads. Runs once (guarded by !payCurrency, so no render loop).
  useEffect(() => {
    const book = state.accounting_book_details ? state.accounting_book_details.currency : null;
    if (book && !payCurrency) { setPayCurrency(book); }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.accounting_book_details, payCurrency]);

  // Seed the book's 常用 currencies for the dropdown sections.
  useEffect(() => {
    const d = state.accounting_book_details;
    if (d && Array.isArray(d.favorite_currencies)) { setFavorites(d.favorite_currencies); }
  }, [state.accounting_book_details]);

  // Star toggle in the dropdown: promote/demote a 常用 currency and persist it
  // on the book (optimistic; fire-and-forget).
  const handleToggleFavorite = (code) => {
    const next = favorites.includes(code) ? favorites.filter((c) => c !== code) : [...favorites, code];
    setFavorites(next);
    if (authState && authState.api && group_id && accounting_book_id) {
      authState.api
        .updateAccountingBook(group_id, accounting_book_id, { accounting_book: { favorite_currencies: next } })
        .catch(() => {});
    }
  };

  useEffect(()=> {
    if (_allocationType === 1) {
      setCopiesSummary(_manualOwers);
    }
    // NOTE: setCopiesSummary is intentionally NOT in the deps — it's a const
    // declared further down, so referencing it here (the dep array is evaluated
    // during render) throws a temporal-dead-zone ReferenceError and blanks the
    // whole page. The sibling effect below omits it for the same reason.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [_manualOwers, _allocationType]);

  useEffect(()=> {
    if (use_multi_payer && _payerAllocationType === 1) {
      setCopiesSummary(_customPayers);
    }
  }, [_customPayers, _payerAllocationType]);


  const isValidCopies = (objects) => {
    let sum = 0;
    let summaryValid = true;

    objects.value.forEach(o => {
      if (o.copies !== undefined) {
        sum += o.copies;
      }
    });
    if (sum === 0) {
      summaryValid = false;
    }

    return summaryValid;
  };

  const setCopiesSummary = (m_owers) => {
    setSummary({
      message: '請輸入份數',
      valid: isValidCopies(m_owers)
    });
  };

  useEffect(() => {
    if (!formTouched) {
      return;
    }

    if (_allocationType === 0 || _payerAllocationType === 0) {
      var summaryValid = true;

      if (fixedAmount != undefined && fixedAmount > 0) {
        if (use_multi_payer) {
          summaryValid = parseFloat(fixedAmount) === parseFloat(summaryAmount) && parseFloat(fixedAmount) === parseFloat(payerSummaryAmount);
        } else {
          summaryValid = parseFloat(fixedAmount) === parseFloat(summaryAmount);
        }
      } else {
        summaryValid = false;
      }

      setSummary({
        message: '總額與分帳小計不符，請調整',
        valid: summaryValid
      });
    }
  }, [formTouched, fixedAmount, payerSummaryAmount, summaryAmount, _allocationType, _payerAllocationType]);

  const getSummaryValid = (alloType, sumAmount, objects) => {
    let valid = true;
    if (alloType === 0) {
      if (fixedAmount && fixedAmount > 0) {
        valid = parseFloat(fixedAmount) === parseFloat(sumAmount);
      }
    } else {
      valid = isValidCopies(objects);
    }
    return valid;
  };

  let payerSumValid = getSummaryValid(_payerAllocationType, payerSummaryAmount, _customPayers);
  let alloSumValid = getSummaryValid(_allocationType, summaryAmount, _manualOwers);

  // Multi-payer with no fixed total (amounts built from rows): the API rejects
  // a payment whose payer allocations don't sum to its ower allocations
  // (raise "payers and owers amount not match"). When there's no fixedAmount to
  // anchor both sides, guard it here in TWD so submit stays disabled until they
  // match. This matters most with foreign input, where per-row rounding can make
  // the two sides drift apart.
  if (use_multi_payer && !fixedAmount && _payerAllocationType === 0 && _allocationType === 0
    && payerSummaryAmount > 0 && summaryAmount > 0) {
    const sidesMatch = parseFloat(payerSummaryAmount) === parseFloat(summaryAmount);
    payerSumValid = payerSumValid && sidesMatch;
    alloSumValid = alloSumValid && sidesMatch;
  }
  let summaryValid = payerSumValid && alloSumValid;

  const [payer, payerLabel] = useUserRadioSelectLabel({
    users: _users,
    initialValue: state.payer.value,
  });

  const nameInput = <TextInput
    style={{ paddingTop: '16px' }}
    key='name'
    svg={<Svg icon='Payment' size='24' className='gold900'/> }
    disabled={false}
    placeholder='輸入款項'
    name={'款項'}
    changed={(v) => _setName({ value: v, valid: v.length > 0 })}
    value={_name.value}
    valid={_name.valid}
    invalidFeedback="內容不可為空，12字內"
    type='text'
  />;

  const datePickerInput = <DatePickerInput
    name='日期'
    changed={(v) => _setCreationDate({ value: v, valid: true })}
    value={_creation_date.value}
    svg={<Svg icon='Date' size='24' className='gold900'/> }
    />;

  const applyAmount = (v) => {
    setFormTouched(true);
    _setAmount({ value: v, valid: true, touched: true });

    if (v.length === 0) {
      setFixedAmount(null);
    } else {
      setFixedAmount(v);
    }
    triggerPayersReallocation(v);
    triggerOwersReallocation(v);
  };

  // Manual edits in settlement currency: the typed value IS the settlement
  // amount, no origin metadata.
  const handleAmountChanged = (v) => {
    setOriginMeta(null);
    applyAmount(v);
  };

  const _bookCurrency = () => (state.accounting_book_details ? state.accounting_book_details.currency : null);
  const _bookExponent = () => (state.accounting_book_details ? (state.accounting_book_details.exponent || 0) : 0);
  const _roundTo = (n, e) => { const f = Math.pow(10, e); return Math.round(n * f) / f; };

  // --- foreign-currency layer ---------------------------------------------
  // When a non-settlement currency is selected we FLIP the canonical currency:
  // the amount field and every allocation are kept in the SELECTED currency, so
  // the rows and 小計 always sum exactly to what the user typed. The settlement
  // amounts the BE needs (and the payer_sum == ower_sum invariant it enforces)
  // are derived at submit time via apportion() in PaymentContext. `origin_*`
  // carries the selected-currency total + rate.
  const bookCurrency = _bookCurrency();
  const bookExponent = _bookExponent();
  const bookSymbol = state.accounting_book_details ? (state.accounting_book_details.currency_symbol || '$') : '$';
  const isForeign = !!payCurrency && !!bookCurrency && payCurrency !== bookCurrency;
  const liveRate = isForeign ? unitRate(payCurrency, bookCurrency, fxRates) : null;
  // Rate (settlement units per 1 selected unit). Prefer the rate captured in
  // originMeta so editing an existing payment uses its *stored* rate (not
  // today's live rate); fall back to the live rate for fresh input.
  const displayRate = isForeign ? ((originMeta && Number(originMeta.exchange_rate)) || liveRate) : null;
  const displayExponent = isForeign && currencyMeta[payCurrency] ? currencyMeta[payCurrency].exponent : bookExponent;
  const displaySymbol = isForeign && currencyMeta[payCurrency] ? currencyMeta[payCurrency].symbol : bookSymbol;
  // The exponent the editor's split/sum/round math runs in: the selected
  // currency's when foreign, otherwise the book's.
  const workingExponent = isForeign ? displayExponent : bookExponent;

  // When the (selected-currency) total is rebuilt from per-row allocations,
  // keep the foreign origin metadata in sync with that running total.
  const syncForeignOrigin = (foreignSum) => {
    if (!isForeign || !displayRate) { return; }
    const s = parseFloat(foreignSum);
    if (!isFinite(s) || s <= 0) {
      setForeignAmount('');
      setOriginMeta(null);
      return;
    }
    setForeignAmount(String(s));
    setOriginMeta({ origin_currency: payCurrency, origin_amount: s, exchange_rate: displayRate });
  };

  const handleForeignAmountChanged = (v) => {
    setForeignAmount(v);
    const rate = displayRate || unitRate(payCurrency, bookCurrency, fxRates);
    const f = parseFloat(v);
    if (!isFinite(f) || f <= 0) {
      applyAmount('');
      setOriginMeta(null);
      return;
    }
    // The typed value IS the working (selected-currency) amount; allocations are
    // split in this currency. Settlement is derived later, at submit.
    applyAmount(String(f));
    setOriginMeta({ origin_currency: payCurrency, origin_amount: f, exchange_rate: rate });
  };

  // Inline currency dropdown change. We pivot the current working amount through
  // the settlement currency so switching keeps roughly the same real value.
  const handleCurrencyChange = (code) => {
    const book = _bookCurrency();
    const current = parseFloat(_amount.value);
    // Current working amount expressed in the settlement currency.
    const curSettlement = (isForeign && displayRate && isFinite(current))
      ? current * displayRate
      : (isFinite(current) ? current : NaN);

    if (!code || code === book) {
      // Back to the settlement currency: amount becomes the settlement value.
      setPayCurrency(book);
      setForeignAmount('');
      setOriginMeta(null);
      if (isFinite(curSettlement) && curSettlement > 0) {
        applyAmount(String(_roundTo(curSettlement, bookExponent)));
      }
      return;
    }

    const rate = unitRate(code, book, fxRates);
    const exp = currencyMeta[code] ? currencyMeta[code].exponent : bookExponent;
    setPayCurrency(code);
    const seed = (rate && isFinite(curSettlement) && curSettlement > 0)
      ? _roundTo(curSettlement / rate, exp) : '';
    if (seed === '' || seed <= 0) {
      setForeignAmount('');
      setOriginMeta(null);
      applyAmount('');
      return;
    }
    setForeignAmount(String(seed));
    applyAmount(String(seed));
    setOriginMeta({ origin_currency: code, origin_amount: seed, exchange_rate: rate });
  };

  // Autofill from a scanned 電子發票: total amount -> 金額, ROC date -> 日期,
  // and the first item name -> 款項 when it decoded to readable text.
  const handleInvoiceScanned = (parsed) => {
    if (parsed.amount > 0) {
      handleAmountChanged(String(parsed.amount));
    }
    if (parsed.isoDate) {
      _setCreationDate({ value: parsed.isoDate, valid: true });
    }
    if (parsed.description) {
      _setName({ value: parsed.description, valid: true });
    }
  };

  const triggerPayersReallocation = (amount) => {
    let touched = false;
    _customPayers.value.forEach(o => {
      if (o.touched) {
        touched = true;
      }
    });

    if (!touched) {
      _setCustomPayers(
        getCustomPayersAmount(
          users, _payers.value, _customPayers.value.map(o => o.user.id), _customPayers, amount
        ).state,
      );
      setPayerSummaryAmount(amount);
    } else {
      let validIds = _customPayers.value.filter(o => o.amount > 0).map(o => o.user.id);
      _setPayers({ value: users.filter(u => validIds.includes(u.id)), valid: true });
    }
  };

  const triggerOwersReallocation = (amount) => {
    let touched = false;

    _manualOwers.value.forEach(o => {
      if (o.touched) {
        touched = true;
      }
    });

    if (!touched) {
      // _setManualOwers(getManualOwersAmount(users, _owers.value, _manualOwers.value.filter(o => !o.user.touched).map(o => o.user.id), v).state)
      _setManualOwers(
        getManualOwersAmount(
          users, _owers.value, _manualOwers.value.map(o => o.user.id), _manualOwers, amount
        ).state,
      );
      setSummaryAmount(amount);
    } else {
      let validManualOwersIds = _manualOwers.value.filter(o => o.amount > 0).map(o => o.user.id);
      _setOwers({ value: users.filter(u => validManualOwersIds.includes(u.id)), valid: true });
    }
  };

  const handleOwersChanged = (objs) => {
    let currentOwers = _owers.value;
    _setOwers({ value: objs, valid: true, touched: true });

    setFormTouched(true);
    if (objs.length === 0 && !fixedAmount) {
      _setAmount({ value: 0, valid: true });
    }
  };

  const handlePayersChanged = (objs) => {
    let currentPayers = _payers.value;
    _setPayers({ value: objs, valid: true, touched: true });

    setFormTouched(true);
    if (objs.length === 0 && !fixedAmount) {
      _setAmount({ value: 0, valid: true });
    }
  };

  const getCustomPayersAmount = (users, owers, unTouchedIds, valueObjects, totalAmount) => {
    let selectedOwerIds = owers.map(o => o.id);
    let copies_objects = valueObjects.value.filter(el => el.copies > 0);
    let copies_objects_ids = copies_objects.map(o => o.user.id);

    let amount_array = split_into(totalAmount, selectedOwerIds.length, workingExponent);

    let i = 0;
    let m_owers = users.map(u => {
      let amount;

      if (!totalAmount || totalAmount === 0) {
        amount = '';
      } else if (selectedOwerIds.includes(u.id)) {
        amount = amount_array[i];
        i++;
      } else {
        amount = '';
      }

      let copies = 0;
      if (copies_objects_ids.includes(u.id)) {
        copies = copies_objects.filter(o => (o.user.id === u.id))[0].copies;
      }

      return {
        user: u,
        amount: amount,
        copies: copies
      };
    });

    return({
      state: {
        value: m_owers,
        valid: true
      },
      splits: amount_array
    }
    );
  };

  const getManualOwersAmount = (users, selectedObjs, unTouchedIds, customObjects, totalAmount) => {
    let selectedIds = selectedObjs.map(o => o.id);
    let copies_objects = customObjects.value.filter(el => el.copies > 0);
    let copies_objects_ids = copies_objects.map(o => o.user.id);

    let amount_array = split_into(totalAmount, selectedIds.length, workingExponent);

    let i = 0;
    let c_objs = users.map(u => {
      let amount;

      if (!totalAmount || totalAmount === 0) {
        amount = '';
      } else if (selectedIds.includes(u.id)) {
        amount = amount_array[i];
        i++;
      } else {
        amount = '';
      }

      let copies = 0;
      if (copies_objects_ids.includes(u.id)) {
        copies = copies_objects.filter(o => (o.user.id === u.id))[0].copies;
      }

      return {
        user: u,
        amount: amount,
        copies: copies
      };
    });

    return({
      state: {
        value: c_objs,
        valid: true
      },
      splits: amount_array
    }
    );
  };

  const handleSetPayerAmount = (object_id, value) => {
    if (_payerAllocationType != 0) {
      return;
    }

    let newObjects = [..._customPayers.value];
    let index = newObjects.findIndex(o => o.user.id === object_id);

    newObjects[index] = {
      user: newObjects[index].user,
      amount: value,
      touched: true
    };

    if (!fixedAmount) {
      let sum = sumPaymentObjects(newObjects, workingExponent);
      _setAmount({ value: sum, valid: sum > 0 });
      syncForeignOrigin(sum);
      triggerOwersReallocation(sum);
    }
  };


  const handleSetOwerAmount = (object_id, value) => {
    if (_allocationType != 0) {
      return;
    }

    let newOwers = [..._manualOwers.value];
    let index = newOwers.findIndex(o => o.user.id === object_id);

    newOwers[index] = {
      user: newOwers[index].user,
      amount: value,
      touched: true
    };

    if (!fixedAmount) {
      let sum = sumOwers(newOwers, workingExponent);
      _setAmount({ value: sum, valid: sum > 0 });
      syncForeignOrigin(sum);
      triggerPayersReallocation(sum);
    }
  };

  // (bookCurrency / bookExponent / bookSymbol / isForeign / liveRate / displayRate
  // are declared above with the foreign-currency display layer.)
  const convertedSettlement = (isForeign && liveRate && parseFloat(foreignAmount) > 0)
    ? Math.round(parseFloat(foreignAmount) * liveRate * Math.pow(10, bookExponent)) / Math.pow(10, bookExponent)
    : null;
  const convertedDisplay = convertedSettlement !== null
    ? convertedSettlement.toLocaleString(undefined, { maximumFractionDigits: bookExponent })
    : '—';

  const amountInput = <TextInput
    key='amount'
    svg={<Svg icon='Money' size='24' className='gold900'/> }
    disabled={false}
    placeholder='輸入金額'
    name={'金額'}
    style={{ borderRadius: radius.lg }}
    changed={isForeign ? handleForeignAmountChanged : handleAmountChanged}
    value={isForeign ? foreignAmount : _amount.value}
    valid={summary.valid && _amount.valid}
    invalidFeedback={!summary.valid ?  '' : '內容不可為空，小於100000000'}
    type='number'
  />;

  const handleAmountLabelsOpen = () => {
  };

  const handleDrawerClosed = () => {
    if (_amount.value > 0) {
      setFixedAmount(_amount.value);
    }
  };

  const payersLabel = <PayerEditableCheckboxSelectLabel
    exponent={workingExponent}
    bookExponent={bookExponent}
    setSummaryAmount={setPayerSummaryAmount}
    getManualOwersAmount={getCustomPayersAmount}
    setOwerAmount={handleSetPayerAmount}
    fixedAmount={fixedAmount}
    allocationType={_payerAllocationType}
    setAllocationType={_setPayerAllocationType}
    users={_users ? _users.filter(u => u.coverCost === true) : []}
    manualOwers={_customPayers}
    selectChanged={handlePayersChanged}
    setManualOwers={_setCustomPayers}
    valid={payerSumValid}
    selectedObjects={_payers.value}
    handleOpen={handleAmountLabelsOpen}
    handleDrawerClosed={handleDrawerClosed}
    isForeign={isForeign}
    displaySymbol={displaySymbol}
    displayExponent={displayExponent}
    displayRate={displayRate}
    displayCode={payCurrency}
    bookSymbol={bookSymbol}
  />;

  const owersLabel = <OwerEditableCheckboxSelectLabel
    exponent={workingExponent}
    bookExponent={bookExponent}
    setSummaryAmount={setSummaryAmount}
    getManualOwersAmount={getManualOwersAmount}
    setOwerAmount={handleSetOwerAmount}
    fixedAmount={fixedAmount}
    allocationType={_allocationType}
    setAllocationType={_setAllocationType}
    users={_users ? _users.filter(u => u.coverCost === true) : []}
    manualOwers={_manualOwers}
    selectChanged={handleOwersChanged}
    setManualOwers={_setManualOwers}
    valid={alloSumValid}
    selectedObjects={_owers.value}
    handleOpen={handleAmountLabelsOpen}
    handleDrawerClosed={handleDrawerClosed}
    isForeign={isForeign}
    displaySymbol={displaySymbol}
    displayExponent={displayExponent}
    displayRate={displayRate}
    displayCode={payCurrency}
    bookSymbol={bookSymbol}
  />;

  const handleManualOwersChanged = (index, data) => {
    let newOwers = [..._manualOwers.value];
    newOwers[index] = data;

    let objects = newOwers.filter(object => object.amount > 0);
    let amount = objects.reduce((prev, object) => {
      if (parseFloat(object.amount) > 0) { return prev + parseFloat(object.amount); }
      else { return (prev + 0); }
    }, 0);

    let valid = newOwers.filter(ower => ower.amount > 0).length === newOwers.length;

    _setManualOwers({ value: newOwers, valid: valid });
  };

  useEffect(() => {
    if (_owers.value.length === 0) {
      _setOwers({ value: owers, valid: true });
      _setManualOwers({ value: manualOwers, valid: true });
    }
  }, [owers]);

  useEffect(() => {
    if (payment) {
      let payers = customPayers.filter(o => o.amount > 0).map(o => o.user);
      _setPayers({ value: payers, valid: true });
      _setCustomPayers({ value: customPayers, valid: true });
    } else {
      if (_payers.value.length === 0) {
        _setPayers({ value: [state.payer.value], valid: true });
        _setCustomPayers({ value: customPayers, valid: true });
      }
    }
  }, [customPayers, payment]);

  const form = {
    evenly: ['name', 'amount', 'payer', 'owers', 'creation_date'],
    amount: ['name', 'payer', 'amount', 'manualOwers', 'creation_date'],
    multiPayerAmount: ['name', 'customPayers', 'amount', 'manualOwers', 'creation_date'],
  };

  const buildPaymentSuccessMessage = (message, log_message_category, accounting_book_name) => {
    let contents = {
      'type': 'bubble',
      'size': 'mega',
      'body': {
        'type': 'box',
        'layout': 'vertical',
        'contents': [
          {
            'type': 'box',
            'layout': 'baseline',
            'contents': [
              {
                'type': 'text',
                'text': `${log_message_category} 至`,
                'color': '#C8842A',
                'align': 'start',
                'size': 'xs',
                'gravity': 'center',
                'weight': 'bold',
                'style': 'normal',
                'position': 'relative',
                'decoration': 'none',
                'wrap': true,
                'flex': 0
              },
              {
                'type': 'text',
                'text': accounting_book_name,
                'align': 'start',
                'size': 'xs',
                'gravity': 'center',
                'weight': 'bold',
                'style': 'normal',
                'position': 'relative',
                'decoration': 'none',
                'wrap': true,
                'flex': 0,
                'margin': 'md'
              }
            ]
          },
          {
            'type': 'box',
            'layout': 'vertical',
            'contents': [
              {
                'type': 'text',
                'text': message,
                'size': 'sm',
                'wrap': true
              }
            ],
            'paddingTop': 'xs'
          }
        ],
        'backgroundColor': '#ffffff',
        'paddingAll': 'xl'
      },
      'styles': {
        'header': {
          'separator': true
        },
        'footer': {
          'separator': true
        }
      }
    };

    let flex_message = {
      'type': 'flex',
      'altText': '新增款項',
      'contents': contents
    };

    return flex_message;
  };


  const getManualOwers = () => {
    if (_allocationType === 0) {
      return _manualOwers;
    }

    if (_allocationType === 1) {
      return getManualOwersBasedOnCopies(_manualOwers);
    }
  };

  const getCustomPayers = () => {
    if (_payerAllocationType === 0) {
      return _customPayers;
    }
    if (_payerAllocationType === 1) {
      return getManualOwersBasedOnCopies(_customPayers);
    }
  };

  const getManualOwersBasedOnCopies = (baseObjects) => {
    if (!_amount.value) {
      return baseObjects;
    }

    let sum = baseObjects.value.reduce((accumulator, object) => {
      if (object.copies != undefined) {
        return accumulator + object.copies;
      } else {
        return accumulator;
      }
    }, 0);

    let ary = split_into(_amount.value, sum, workingExponent);

    let newObjects = baseObjects.value.map(o => {
      if (o.copies > 0) {
        let new_ary = ary.splice(0, o.copies);
        let amount = new_ary.reduce((acc, obj) => {
          return acc + obj;
        });

        o.amount = amount;

      } else {
        o.amount = 0;
      }

      return o;
    });

    let new_sum = newObjects.reduce((accumulator, object) => {
      if (object.amount != undefined) {
        return accumulator + object.amount;
      } else {
        return accumulator;
      }
    }, 0);

    return { value: newObjects, valid: new_sum > 0 };
  };

  const resetAll = () => {
    _setAllocationType(0);
    _setPayerAllocationType(0);
    resetForm();
  };
  const redirectToSettingPage = () => {
    history.navigateTo('accountingBookSettingsPage', { group_id, accounting_book_id });
  };

  const handleSubmit = () => {
    let newState = {
      ...state,
      manualOwers: getManualOwers(),
      customPayers: getCustomPayers(),
      owers: _owers,
      payer: { value: payer, valid: true },
      name: _name,
      creation_date: _creation_date,
      amount: _amount,
      allocation_type: 'amount',
      use_multi_payer: use_multi_payer,
      origin_meta: originMeta
    };

    let valid = false;
    if (use_multi_payer) {
      valid = validateForm(newState, form['multiPayerAmount'], validate);
    } else {
      valid = validateForm(newState, form['amount'], validate);
    }
    if (!valid) {
      console.log(newState);
      console.log('[Debug] payment invalid');
      return;
    }

    setDisableForm(true);
    setLoading(true);

    createPayment(authState.api, newState, (data) => {
      if (data.status === 'bad_request') {
        resetAll();
        alert(data.message);
      } else {
        if (data.send_liff_confirm_message === true) {
          if (liff.isInClient()) {
            let message = buildPaymentSuccessMessage(data.log_message_content, data.log_message_category, data.accounting_book_name);
            let messages = [message];
            liff.sendMessages(messages)
              .then(() => {});
          }
        }
        resetAll();
        // Desktop master-detail keeps the editor in place and refreshes the
        // list via onComplete; otherwise return to wherever the form was opened.
        if (onComplete) { onComplete(data); }
        else if (window.history.length > 2) { routerHistory.goBack(); }
        else { history.navigateTo('paymentIndexPage', { group_id, accounting_book_id }); }
      }
    });
  };

  const setFunction = {
    'name': _setName,
    'manualOwers': _setManualOwers,
    'amount': _setAmount,
    'owers': _setOwers
  };

  const validate = (newState) => {
    if (!newState) { return; }

    ['name', 'amount'].forEach(el => {
      if (newState[el] && !newState[el].valid) {
        setFunction[el]({ value: newState[el].value, valid: false });
      }
    });
  };

  let disabled = true;
  if (!summary.valid) {
    disabled = true;
  } else {
    disabled = disableForm;
  }

  let payersSummary = null;
  if (use_multi_payer && !loading) {
    if (debug) {
      console.log(`payerSummaryAmount: ${payerSummaryAmount}`);
      console.log(`fixedAmount: ${fixedAmount}`);
      console.log(`payerSumValid: ${payerSumValid}`);
    }
    if (_allocationType == 0 && state.accounting_book_details && payerSummaryAmount > 0) {
      payersSummary = <div style={{ color: payerSumValid ? themeColors.green700 : themeColors.invalid, fontFamily: 'Noto Sans TC' }}>
        { `小計： ${displaySymbol}${payerSummaryAmount}`
        }
      </div>;
    }

    if (_payerAllocationType === 1) {
      payersSummary = <div style={{ color: payerSumValid ? themeColors.green700 : themeColors.invalid, fontFamily: 'Noto Sans TC' }}>
        以份數分配
      </div>;
    }
  }

  let allocationSummary = null;
  if (!loading) {
    if (_allocationType === 0 && state.accounting_book_details && summaryAmount > 0) {
      allocationSummary = <div style={{ color: alloSumValid ? themeColors.green700 : themeColors.invalid, fontFamily: 'Noto Sans TC' }}>
        {
          `小計： ${displaySymbol}${summaryAmount}`
        }
      </div>;
    }

    if (_allocationType === 1) {
      allocationSummary = <div style={{ color: alloSumValid ? themeColors.green700 : themeColors.invalid, fontFamily: 'Noto Sans TC' }}>
        以份數分配
      </div>;
    }
  }

  return(
    <>
      <div className={styles.container}>
        <div className={styles.stepContainer}>
          <div style={{ position: 'relative' }}>
            { nameInput }
            <InvoiceScanner onScanned={handleInvoiceScanned} />
          </div>
          { alertMessage }
          <div style={{ position: 'relative' }}>
            { amountInput }
            {
              (bookCurrency && fxRates) ?
                <div style={{ position: 'absolute', top: 0, height: '48px', right: '16px', display: 'flex', alignItems: 'center' }}>
                  <CurrencyDropdown
                    value={payCurrency || bookCurrency}
                    bookCurrency={bookCurrency}
                    favorites={favorites}
                    onChange={handleCurrencyChange}
                    onToggleFavorite={handleToggleFavorite}
                  />
                </div> : null
            }
          </div>
          {
            isForeign ?
              <div style={{ margin: '-12px 0 14px', padding: '0 16px', fontSize: '13px', color: themeColors.gray600 }}>
                ⇄ 換算後 ≈ <strong style={{ color: themeColors.green500 }}>{bookSymbol}{convertedDisplay}</strong>
                {liveRate
                  ? <span style={{ color: themeColors.gray500, marginLeft: '6px' }}>（1 {payCurrency} = {liveRate} {bookCurrency}）</span>
                  : <span style={{ color: themeColors.gray500, marginLeft: '6px' }}>匯率載入中…</span>}
              </div> : null
          }
          { datePickerInput }
          {
            use_multi_payer ?
              <>
                <Section name="付款者" style={{ marginTop: '16px' }}>
                  {
                    payersSummary
                  }
                </Section>
                { payersLabel }
              </> :
              <>
                <Section name="付款者"/>
                { payerLabel }
              </>
          }
          <Section name="分款者" style={{ marginTop: '16px' }}>
            {
              allocationSummary
            }
          </Section>
          { owersLabel }
        </div>

        {

          locked ?
            <div className={styles.footer}>
              <Button color="green" clicked={redirectToSettingPage} disabled={(disabled || loading)}>前往解鎖帳本</Button>
              <div style={{
                color: themeColors.invalid,
                fontSize: '12px',
                textAlign: 'center',
                paddingTop: '6px',
                opacity:  summary.valid ? '0%' : '100%'
              }}>
                {summary.message}
              </div>
            </div> :
            <div className={styles.footer}>
              <Button clicked={handleSubmit} disabled={(disabled || loading)}>建立帳款</Button>
              <div style={{
                color: themeColors.invalid,
                fontSize: '12px',
                textAlign: 'center',
                paddingTop: '6px',
                opacity:  summary.valid ? '0%' : '100%'
              }}>
                {summary.message}
              </div>
            </div>
        }

      </div>
      {
        (disableForm || loading) ? <FullPageLoader /> : null
      }
    </>
  );
};

  const defaultStyles = {
    root: {
      flexGrow: 1,
      maxHeight: '-webkit-fill-available',
      margin: 0,
      height: 'calc(100% - 49px - 84px)',
      overflow: 'hidden',
    },
    slideContainer: {
      height: 'calc(100%)',
      margin: 0,
      overflow: 'hidden',
    },
    slide: {
      overflow: 'auto',
      height: 'calc(100%)',
      backgroundColor: '#FFFFFF',
    },
  };
export default PaymentForm;
